import React from 'react';
import { toaster } from 'baseui/toast';
import type { RangeSpanT } from '../../../../../common/types/types';
import { SEQUENCE_MODAL_TYPE } from '../../../../labelling/components/frames-control-panel/frames-timeline/modernised-annotation-timeline-modal/constants';
import { SequenceOverrideConfirmationModal } from '../../../../labelling/components/frames-control-panel/frames-timeline/sequence-override-confirmation-modal';
import { getUpdatedAnnotationSequence } from '../../../../labelling/components/frames-control-panel/frames-timeline/utils';
import { useSamForTracking } from '../../../../labelling/providers/hooks/use-sam-for-tracking';
import type { AnnotationType } from '../../../../labelling/types/annotations.type';
import { TOAST_AUTO_HIDE_MS } from '../../../constants/frame-timeline-v4';
import type { KeyframeEntryT } from '../../../studio-store/types';
import type { TrackSpanT } from '../../../types/frame-timeline-v4';
import { hasKeyframeInRange } from '../../../utils/frame-span-utils';
import { AnnotationSpanPopoverContent } from './annotation-span-popover-content';
import { SpanPopover } from './span-popover';
const TRACKABLE_TYPES = new Set(['SEGMENTATION', 'RECTANGLE']);
type AnnotationSpanActionsPropsT = {
  annotationId: string;
  span: TrackSpanT;
  annotationType?: AnnotationType;
  keyframes: KeyframeEntryT[];
  labelTitle: string;
  onTrackAnnotation?: (params: {
    annotationId: string;
    annotationType: AnnotationType;
    annotationSequence: Map<string, RangeSpanT>;
  }) => Promise<void>;
  onDeleteAnnotationSpan?: (params: {
    annotationIdx: string;
    frameSpan: {
      start: number;
      end: number;
    };
  }) => void;
  children: React.ReactNode;
};
export const AnnotationSpanActions = ({
  annotationId,
  span,
  annotationType,
  keyframes,
  labelTitle,
  onTrackAnnotation,
  onDeleteAnnotationSpan,
  children
}: AnnotationSpanActionsPropsT) => {
  const isTrackable = !!onTrackAnnotation && !!annotationType && TRACKABLE_TYPES.has(annotationType);
  const trackingRange = {
    start: span.start + 1,
    end: span.end
  };
  const {
    isTracking,
    showOverrideModal,
    startTracking,
    confirmOverride,
    cancelOverride
  } = useSamForTracking({
    trackingRange,
    hasConflicts: range => hasKeyframeInRange(range.start, range.end, keyframes),
    onTrack: async range => {
      toaster.info('Tracking started', {
        autoHideDuration: TOAST_AUTO_HIDE_MS
      });
      const {
        updatedAnnotationSequence
      } = getUpdatedAnnotationSequence(range, new Map());
      await onTrackAnnotation?.({
        annotationId,
        annotationType: annotationType!,
        annotationSequence: updatedAnnotationSequence
      });
    },
    onTrackError: error => {
      toaster.negative(error, {
        autoHideDuration: TOAST_AUTO_HIDE_MS
      });
    }
  });
  return <>
      <SpanPopover isDismissable={!isTracking} content={({
      close
    }) => <AnnotationSpanPopoverContent annotationId={annotationId} span={span} close={close} onDelete={onDeleteAnnotationSpan} onTrack={isTrackable ? startTracking : undefined} isTracking={isTracking} />}>
        {children}
      </SpanPopover>
      {isTrackable && <SequenceOverrideConfirmationModal isOpen={showOverrideModal} onConfirm={confirmOverride} onCancel={cancelOverride} startFrame={trackingRange.start} endFrame={trackingRange.end} labelTitle={labelTitle} isLoading={isTracking} type={SEQUENCE_MODAL_TYPE.TRACK} />}
    </>;
};